// The standard patterns (#166 "Insular patterns": blob, fence, rug; plus the Oryx-specific wall16,
// ground and shore adapters). Each takes a neighbourhood mask and returns frame indices.
import { E, N, S, W, NE, SE, SW, NW, innerCorners, mask4 } from './grid';
import { GROUND_CORNER_LIGHT, GROUND_EDGE_LIGHT, GROUND_EDGE_LIGHT_SHARE, GROUND_FILL_WEIGHTS, GROUND_FILLS, GROUND_IN, GROUND_LIP, LIQUID_FILL, ROW, SHORE, SHORE_COMBO, SHORE_NUB, WALL16, WALL_CRACKED, type DungeonKitRow, type GroundSet, type LiquidSet } from './oryx';
import { weightedIndex } from './hash';
import type { Variants } from './types';

export function pickVariant(v: Variants, roll: number): number | undefined {
  if (v.length === 0) return undefined;
  return v[weightedIndex(v.map((x) => x[1]), roll)]![0];
}

/** Oryx wall kit: the piece for a 4-bit mask of connected walls; cracked variants by roll. */
export function wallPiece(row: DungeonKitRow, mask: number, roll: number, cracked = true): number {
  const m4 = mask4(mask);
  if (cracked) {
    const c = WALL_CRACKED[m4];
    if (c !== undefined && roll % 7 === 0) return row.wallBase + c;
  }
  return row.wallBase + WALL16[m4]!;
}

/** The ground fill for a cell that is ground on all four sides (plain or flecked, by roll). */
export function groundFill(set: GroundSet, roll: number): number {
  return set.base + GROUND_FILLS[weightedIndex(set.fillWeights ?? GROUND_FILL_WEIGHTS, roll)]!;
}

/**
 * Oryx ground set, inward side: the frame drawn on a ground cell given the 8-bit mask of
 * same-ground neighbours. A fill when all four cardinals are ground (the set has no inner-corner
 * pieces, so diagonals do not matter); otherwise the piece for the 4-bit mask, with the light
 * (~1 px) alternates mixed into straight edges and outer corners so long runs do not repeat.
 * Use only in `edges: 'inward'` kits — never together with `groundLips` on the same boundary.
 */
export function groundInward(set: GroundSet, mask: number, roll: number): number[] {
  const m4 = mask4(mask);
  if (m4 === (N | E | S | W)) return [groundFill(set, roll)];
  const piece = GROUND_IN[m4]!;
  const idx = typeof piece === 'number' ? piece : piece[roll % piece.length]!;
  const light = GROUND_EDGE_LIGHT[idx] ?? GROUND_CORNER_LIGHT[idx];
  if (light !== undefined && set.lightEdges !== false && (roll >>> 3) % 1000 < GROUND_EDGE_LIGHT_SHARE) return [set.base + light];
  return [set.base + idx];
}

/**
 * Oryx ground set, outward side: lips drawn on a *non*-ground cell given the 4-bit mask of
 * ground neighbours. Dedicated piece when one exists, else stacked single-edge lips. The ground
 * cells themselves stay full fills in `edges: 'lips'` kits (the default).
 */
export function groundLips(set: GroundSet, mask: number): number[] {
  const m4 = mask4(mask);
  if (m4 === 0) return [];
  const direct = GROUND_LIP[m4];
  if (direct !== undefined) return [set.base + direct];
  const out: number[] = [];
  for (const b of [N, E, S, W]) if (m4 & b) out.push(set.base + GROUND_LIP[b]!);
  return out;
}

/** Liquid fill frames (A/B ripple pair) for a liquid set. */
export function liquidFill(set: LiquidSet): [number, number] { return [set.base + LIQUID_FILL[0], set.base + LIQUID_FILL[1]]; }

/**
 * Oryx liquid shoreline: overlays for a liquid cell given the 8-bit mask of *same-liquid*
 * neighbours. Land is the complement; the shore piece is picked by the 4-bit land mask and lone
 * inner-corner nubs are added (dedicated combination pieces preferred, singles stacked otherwise).
 */
export function shoreOverlays(set: LiquidSet, sameMask: number): number[] {
  const land4 = (~sameMask) & 0x0f;
  // nubs: the diagonal is land while both adjacent cardinals are liquid (an inner corner)
  const nubs = innerCorners(sameMask);
  const out: number[] = [];
  for (const [l, nb, off] of SHORE_COMBO) if (l === land4 && nb === nubs) return [set.base + off];
  const edge = SHORE[land4];
  if (edge !== undefined) out.push(set.base + edge);
  for (const b of [NW, NE, SW, SE]) if (nubs & b) out.push(set.base + SHORE_NUB[b]!);
  return out;
}

/** Fence/plank pattern: 4-bit mask → frame, for sets that provide straight and end pieces only. */
export function fencePiece(ew: readonly number[], ns: readonly number[], post: number, mask: number, roll: number): number {
  const m4 = mask4(mask);
  const horizontal = (m4 & (E | W)) !== 0;
  const vertical = (m4 & (N | S)) !== 0;
  if (horizontal && !vertical) return ew[roll % ew.length]!;
  if (vertical && !horizontal) return ns[roll % ns.length]!;
  if (!horizontal && !vertical) return post;
  return ew[roll % ew.length]!;
}

export const SHEET_ROW = ROW;
